import React from 'react';
import Layout from '../components/layout/Layout';

const operatingScopes = [
  {
    title: 'Execution Infrastructure',
    description: 'Systems deployment, vendor management, process standardization. We build the machine that runs your operation.',
  },
  {
    title: 'Financial Structure',
    description: 'Treasury, payables, receivables, forecasting, compliance. We make your books investable and export-ready.',
  },
  {
    title: 'Logistics & Supply Chain',
    description: 'Procurement, fulfillment, inventory, distribution, export coordination. We move goods across borders.',
  },
  {
    title: 'Automation & Integration',
    description: 'Workflow automation, system integration, data pipelines. AI as execution multiplier, not product.',
  },
];

const controlDivision = {
  ownerRetains: [
    'Vision and direction',
    'Product and service decisions',
    'Key hires and culture',
    'Capital allocation decisions',
    'Exit and governance',
  ],
  bliuControls: [
    'Operational execution',
    'Process and systems',
    'Vendor and supplier management',
    'Financial operations',
    'Compliance and reporting',
  ],
};

const qualifications = {
  required: [
    'Existing business with demonstrated revenue',
    'Owner willing to exit day-to-day operations',
    'Physical goods, services, or exportable capacity',
    'Sufficient runway for meaningful engagement (12+ months)',
    'Decision-making authority to execute terms',
  ],
  disqualifying: [
    'Pre-revenue companies or concept-stage ventures',
    'App-first or purely digital businesses',
    'Operators seeking advisory or coaching',
    'Unwillingness to surrender operational control',
    'Pitch-deck-only operators without proven demand',
  ],
};

const philosophyPoints = [
  {
    title: 'Standardized Core',
    description: 'Every engagement uses our proven frameworks. We do not reinvent infrastructure.',
  },
  {
    title: 'Limited Customization',
    description: 'Adaptation occurs at the edges. Core systems are non-negotiable.',
  },
  {
    title: 'Compounding Returns',
    description: 'Each engagement improves the system. You benefit from everything we have learned.',
  },
  {
    title: 'No Lock-in',
    description: 'Systems are designed for eventual transfer. We build to hand off.',
  },
];

export function WhatWeRun() {
  return (
    <Layout>
      {/* Header */}
      <section className="section border-b border-border">
        <div className="container-narrow">
          <h1 className="text-display-sm md:text-display">What We Run</h1>
        </div>
      </section>

      {/* Operating Scope */}
      <section className="section border-b border-border">
        <div className="container-narrow">
          <div className="space-y-10">
            <div className="space-y-4">
              <h2 className="text-heading-sm text-muted uppercase tracking-wider">
                Operating Scope
              </h2>
              <p className="text-body-lg text-subtle">
                We assume control of operational functions that owners should not be running.
              </p>
            </div>

            <div className="grid gap-8 md:grid-cols-2">
              {operatingScopes.map((scope, index) => (
                <div key={index} className="space-y-3">
                  <h3 className="text-subheading">{scope.title}</h3>
                  <p className="text-body text-subtle">{scope.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Control Model */}
      <section className="section border-b border-border">
        <div className="container-narrow">
          <div className="space-y-10">
            <div className="space-y-4">
              <h2 className="text-heading-sm text-muted uppercase tracking-wider">
                Division of Control
              </h2>
              <p className="text-body text-subtle">
                This is not collaboration. This is division of labor with clear boundaries.
              </p>
            </div>

            <div className="grid gap-8 md:grid-cols-2">
              <div className="space-y-4">
                <h3 className="text-small text-muted uppercase tracking-wider">
                  Owner Retains
                </h3>
                <ul className="space-y-3">
                  {controlDivision.ownerRetains.map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <span className="text-muted text-small mt-1">→</span>
                      <span className="text-body">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="space-y-4">
                <h3 className="text-small text-muted uppercase tracking-wider">
                  Bliu Interactive Controls
                </h3>
                <ul className="space-y-3">
                  {controlDivision.bliuControls.map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <span className="text-foreground text-small mt-1">→</span>
                      <span className="text-body">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Engagement Thresholds */}
      <section className="section border-b border-border">
        <div className="container-narrow">
          <div className="space-y-10">
            <h2 className="text-heading-sm text-muted uppercase tracking-wider">
              Who Qualifies
            </h2>

            <div className="grid gap-10 md:grid-cols-2">
              <div className="space-y-4">
                <h3 className="text-subheading">Required</h3>
                <ul className="space-y-3">
                  {qualifications.required.map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <span className="text-foreground text-small mt-1">+</span>
                      <span className="text-body text-subtle">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="space-y-4">
                <h3 className="text-subheading">Disqualifying</h3>
                <ul className="space-y-3">
                  {qualifications.disqualifying.map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <span className="text-muted text-small mt-1">×</span>
                      <span className="text-body text-muted">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* System Philosophy */}
      <section className="section">
        <div className="container-narrow">
          <div className="space-y-10">
            <div className="space-y-4">
              <h2 className="text-heading-sm text-muted uppercase tracking-wider">
                How We Build
              </h2>
              <p className="text-body-lg text-subtle">
                We deploy standardized systems. We do not build bespoke solutions for each engagement.
              </p>
            </div>

            <div className="space-y-8">
              {philosophyPoints.map((point, index) => (
                <div key={index} className="flex gap-6">
                  <span className="text-caption text-muted font-mono shrink-0 pt-1">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <div className="space-y-2">
                    <h3 className="text-subheading">{point.title}</h3>
                    <p className="text-body text-subtle">{point.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}

export default WhatWeRun;
